import React, { useEffect, useState } from 'react';
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    TableSortLabel,
    Card,
    CardContent,
    Box,
    Button,
    Typography,
    TableContainer,
    InputAdornment,
    TextField,
    CircularProgress,
    Chip,
    Badge,
    Tooltip,
    Avatar,
    Alert,
} from '@mui/material';
import {
    Search as SearchIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    FiberManualRecord as DotIcon,
    Person as PersonIcon,
} from '@mui/icons-material';
import { request } from '../../../../../../utils/request';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useDebounce } from '../../../../../../utils/useDebounce';
import Brandcrumb from '../../../../../../componentsUiBlock/Brandcrumb';
import DeleteConfirmDialog from '../../../../../../componentsUiBlock/DeleteConfirmDialog';
import { useDeleteDialog } from '../../../../../../utils/useDeleteDialog';
import { Employee } from '../types';
import { usePermissions } from '../../../../../../utils/usePermissions';
import { baseUrlImage } from '../../../../../../utils/apiClient';

interface HeadCell {
    id: keyof Employee | 'full_name' | 'contactInfo' | 'employeeType' | 'action';
    label: string;
    sortable?: boolean;
}

const headCells: HeadCell[] = [
    { id: 'photo', label: 'Photo', sortable: false },
    { id: 'full_name', label: 'Employee', sortable: true },
    { id: 'gender', label: 'Gender', sortable: true },
    { id: 'contactInfo', label: 'Contact', sortable: false },
    { id: 'employeeType', label: 'Employee Type', sortable: true },
    { id: 'job', label: 'Joining Date', sortable: true },
    { id: 'job', label: 'Base Salary (Per Month)', sortable: true },
    { id: 'action', label: 'Action', sortable: false }
];

const EmployeesList: React.FC = () => {
    const { canCreateEmployee, canDeleteEmployee } = usePermissions();
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [loading, setLoading] = useState(true);
    const [order, setOrder] = useState<'asc' | 'desc'>('asc');
    const [orderBy, setOrderBy] = useState<keyof Employee>('first_name');
    const [search, setSearch] = useState('');

    const navigate = useNavigate();
    const debouncedSearch = useDebounce(search, 500);

    useEffect(() => {
        const getEmployeeData = async () => {
            try {
                setLoading(true);
                const params = new URLSearchParams();

                if (debouncedSearch) {
                    params.append('search', debouncedSearch);
                }

                // Get all employees without pagination
                const url = `employee/get-all-employees${params.toString() ? `?${params.toString()}` : ''}`;

                const res = await request({
                    url: url,
                    method: 'get',
                });
                
                if (res.status === 'success') {
                    setEmployees(res.data || []);
                }
            } catch (error: any) {
                console.error(error);
                toast.error(error || 'Failed to fetch employees');
            } finally {
                setLoading(false);
            }
        };
        getEmployeeData();
    }, [debouncedSearch]);

    const handleSort = (property: HeadCell['id']) => {
        if (!headCells.find(cell => cell.id === property)?.sortable) return;

        let actualProperty: keyof Employee;

        switch (property) {
            case 'full_name':
                actualProperty = 'first_name';
                break;
            case 'employeeType':
                actualProperty = 'job';
                break;
            default:
                actualProperty = property as keyof Employee;
        }

        const isAsc = orderBy === actualProperty && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(actualProperty);
    };

    // Client-side sorting
    const sortedEmployees = [...employees].sort((a, b) => {
        let aValue: any;
        let bValue: any;

        if (orderBy === 'job') {
            if (headCells.find(cell => cell.id === 'job')?.label === 'Employee Type') {
                aValue = a.job?.employee_type?.toLowerCase() || '';
                bValue = b.job?.employee_type?.toLowerCase() || '';
            } else {
                aValue = new Date(a.job?.joining_date).getTime();
                bValue = new Date(b.job?.joining_date).getTime();
            }
        } else if (orderBy === 'first_name') {
            aValue = `${a.first_name} ${a.last_name}`.toLowerCase();
            bValue = `${b.first_name} ${b.last_name}`.toLowerCase();
        } else {
            aValue = a[orderBy];
            bValue = b[orderBy];
        }

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return order === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return order === 'asc' ? aValue - bValue : bValue - aValue;
        }

        return 0;
    });

    const deleteEmployee = async (employee: Employee) => {
        try {
            const res = await request({
                url: `employee/delete-employee/${employee._id}`,
                method: "delete",
            });
            if (res?.status === "success") {
                toast.success(res?.message);
                setEmployees(prev => prev.filter(em => em._id !== employee._id));
            } else {
                toast.error(res?.message);
            }
        } catch (error: any) {
            toast.error(error);
        }
    };

    const { open, loadingDelete, itemToDelete, openDialog, closeDialog, confirmDelete } = useDeleteDialog<Employee>({
        onDelete: deleteEmployee,
    });

    const getEmployeeTypeColor = (type: string) => {
        switch (type?.toLowerCase()) {
            case 'full-time':
                return 'success';
            case 'part-time':
                return 'info';
            case 'contract':
                return 'warning';
            case 'daily-wage':
                return 'secondary';
            case 'intern':
                return 'default';
            case 'trainee':
                return 'primary';
            default:
                return 'default';
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatCurrency = (amount: number) => {
        if (!amount && amount !== 0) return '-';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    return (
        <>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
                <Brandcrumb />
            </Box>
            <Card elevation={4}>
                <CardContent sx={{ pb: 1 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
                        {canCreateEmployee() && (
                            <Button
                                variant="contained"
                                size='small'
                                color="primary"
                                onClick={() => navigate("/shopManagement/employeeManagement/employees/addEmployee")}
                                sx={{ flexShrink: 0 }}
                                startIcon={<PersonIcon />}
                            >
                                Add New Employee
                            </Button>
                        )}

                        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
                            <Badge badgeContent={employees.length} showZero color="primary">
                                <PersonIcon color="action" />
                            </Badge>

                            <TextField
                                size="small"
                                placeholder="Search employees..."
                                variant="outlined"
                                sx={{ width: { xs: '100%', sm: 300 } }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon color="action" />
                                        </InputAdornment>
                                    ),
                                }}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </Box>
                    </Box>
                </CardContent>

                <CardContent sx={{ pt: 1 }}>
                    <TableContainer sx={{ maxHeight: 600 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {headCells.map((head) => (
                                        <TableCell key={head.id} sx={{ fontWeight: 'bold' }}>
                                            {head.sortable !== false ? (
                                                <TableSortLabel
                                                    active={orderBy === (head.id === 'full_name' ? 'first_name' : head.id === 'employeeType' ? 'job' : head.id)}
                                                    direction={order === 'asc' ? 'asc' : 'desc'}
                                                    onClick={() => handleSort(head.id)}
                                                >
                                                    {head.label}
                                                </TableSortLabel>
                                            ) : (
                                                head.label
                                            )}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            <CircularProgress size={24} />
                                            <Typography variant="body2" sx={{ mt: 1 }}>
                                                {debouncedSearch ? 'Searching...' : 'Loading employees...'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : sortedEmployees.length > 0 ? (
                                    sortedEmployees.map((employee) => (
                                        <TableRow key={employee._id} hover>
                                            {/* Photo */}
                                            <TableCell>
                                                <Tooltip title={`${employee.first_name} ${employee.last_name}`} arrow>
                                                    <Avatar
                                                        src={employee.photo ? baseUrlImage + employee.photo : ''}
                                                        alt={`${employee.first_name} ${employee.last_name}`}
                                                        sx={{
                                                            width: 40,
                                                            height: 40,
                                                            border: '2px solid',
                                                            borderColor: employee?.userId?.status === 'active' ? 'success.main' : 'error.main'
                                                        }}
                                                    >
                                                        {employee.first_name?.charAt(0)}{employee.last_name?.charAt(0)}
                                                    </Avatar>
                                                </Tooltip>
                                            </TableCell>

                                            {/* Full Name with Status Dot */}
                                            <TableCell>
                                                <Box display="flex" alignItems="flex-start" gap={1}>
                                                    <Tooltip
                                                        title={employee?.userId?.status === 'active' ? 'Active Employee as user' : 'Inactive Employee as user'}
                                                        arrow
                                                        placement="top-start"
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                                                            <DotIcon
                                                                sx={{
                                                                    color: employee?.userId?.status === 'active' ? 'success.main' : 'error.main',
                                                                    fontSize: 10,
                                                                }}
                                                            />
                                                        </Box>
                                                    </Tooltip>
                                                    <Box>
                                                        <Typography variant="body2" fontWeight="bold">
                                                            {employee.first_name} {employee.last_name}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            {/* Gender */}
                                            <TableCell>
                                                <Chip
                                                    label={employee.gender || '-'}
                                                    color={employee.gender === 'Male' ? 'primary' :
                                                        employee.gender === 'Female' ? 'info' : 'default'}
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        height: 20,
                                                        '& .MuiChip-label': {
                                                            px: 1,
                                                        }
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Contact Info */}
                                            <TableCell>
                                                <Box sx={{ maxWidth: 150 }}>
                                                    {employee.contact?.phone?.length > 0 && (
                                                        <Box display="flex" alignItems="center" gap={0.5} sx={{ mb: 0.5 }}>
                                                            <PhoneIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography variant="caption" noWrap title={employee.contact.phone[0].phone_no}>
                                                                {employee.contact.phone[0].phone_no}
                                                            </Typography>
                                                        </Box>
                                                    )}

                                                    {employee.contact?.email?.length > 0 && (
                                                        <Box display="flex" alignItems="center" gap={0.5}>
                                                            <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                                            <Typography variant="caption" noWrap title={employee.contact.email[0]}>
                                                                {employee.contact.email[0]}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                </Box>
                                            </TableCell>

                                            {/* Employee Type */}
                                            <TableCell>
                                                <Chip
                                                    label={employee.job?.employee_type || '-'}
                                                    color={getEmployeeTypeColor(employee.job?.employee_type || '')}
                                                    size="small"
                                                    sx={{
                                                        fontSize: '0.65rem',
                                                        height: 18,
                                                    }}
                                                />
                                            </TableCell>

                                            {/* Joining Date */}
                                            <TableCell>
                                                <Typography variant="body2">
                                                    {formatDate(employee.job?.joining_date)}
                                                </Typography>
                                            </TableCell>

                                            {/* Base Salary */}
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {formatCurrency(employee.job?.base_salary)}
                                                </Typography>
                                            </TableCell>

                                            {/* Action Buttons */}
                                            <TableCell>
                                                <Box display="flex" flexDirection="column" gap={0.8}>
                                                    <Button
                                                        variant="text"
                                                        onClick={() => navigate(`/shopManagement/employeeManagement/employees/${employee._id}`)}
                                                        sx={{
                                                            minWidth: 'auto',
                                                            fontSize: '0.60rem',
                                                            textWrap: 'nowrap',
                                                            py: 0.1,
                                                            px: 2,
                                                            borderRadius: 5,
                                                            border: '.8px solid #7f5ee666',
                                                            '&:hover': { backgroundColor: '#7f5ee666' }
                                                        }}
                                                    >
                                                        View / Edit
                                                    </Button>
                                                    {canDeleteEmployee() && (
                                                        <Button
                                                            size="small"
                                                            variant="text"
                                                            color="error"
                                                            onClick={() => openDialog(employee)}
                                                            sx={{
                                                                minWidth: 'auto',
                                                                fontSize: '0.60rem',
                                                                py: 0.1,
                                                                px: 2,
                                                                borderRadius: 5,
                                                                border: '.8px solid #ff4d6e5e',
                                                                '&:hover': { backgroundColor: '#ff4d6e5e' }
                                                            }}
                                                        >
                                                            Delete
                                                        </Button>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                                            <Typography variant="body1" color="text.secondary">
                                                {search ? `No employees found for "${search}"` : 'No employees available. Add your first employee!'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>

            <DeleteConfirmDialog
                open={open}
                loading={loadingDelete}
                title="Delete Employee"
                description={
                    <>
                        Are you sure you want to delete the employee <strong>"{itemToDelete?.first_name} {itemToDelete?.last_name}"</strong>? <br /><br />
                        <Alert severity="warning" sx={{ fontSize: "12px", py: "0px" }}>This action cannot be undone and all associated data will be permanently removed.</Alert>
                    </>
                }
                onCancel={closeDialog}
                onConfirm={confirmDelete}
            />
        </>
    );
};

export default EmployeesList;